import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CheckSquare,
  Clock,
  Eye,
  PlayCircle,
  RefreshCw,
  ServerCrash,
  Square,
} from 'lucide-react';
import { z } from 'zod';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Drawer } from '@/components/ui/Drawer';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { RelativeTime } from '@/components/ui/RelativeTime';
import { SeverityBadge } from '@/components/ui/SeverityBadge';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import { DataTable } from '@/components/ui/DataTable';
import type { Column } from '@/components/ui/DataTable';
import { DriftFilterBar } from '@/components/drift/DriftFilterBar';
import { useCustomers } from '@/hooks/useCustomers';
import {
  applyDriftClientFilters,
  planFilteredRescan,
  sortDriftRows,
  useDriftReport,
  useHealthCheckJob,
  useRunHealthChecks,
  RESCAN_MAX_SYSTEMS,
  type DriftFilter,
  type DriftRow,
  type DriftSort,
  type DriftSortKey,
  type FilteredRescanPlan,
  type HealthCheckJob,
} from '@/hooks/useDriftReport';
import { useServiceTypes } from '@/hooks/useServiceTypes';
import { iconForServiceType } from '@/lib/serviceIcons';
import { formatDateTime } from '@/lib/format';

/**
 * /drift - Drift-Dashboard (Sprint 4, WT-403/WT-405/WT-406; Filter-Ausbau
 * Sprint 8).
 *
 * Header: 4 Mini-Stat-Cards (Total / Drifted / EOL / Stale).
 * Body: Filter-Toolbar + Tabelle, Click auf Zeile -> Drawer mit Details.
 * Bulk-Action: "Re-Scan ausgewaehlte" / "Re-Scan alle gefilterten".
 *
 * Filter + Sortierung sind URL-State (deep-linkbar, überlebt Reload) im
 * Stil von systems/index.tsx. replace:true, damit die debouncte Freitextsuche
 * die History nicht flutet.
 *
 * Polling-Strategie: nach Re-Scan-Start triggern wir useHealthCheckJob, der
 * sich selbstständig alle 3s holt - sobald status=completed/failed, stoppt
 * das Interval von alleine. Wenn der User das Dashboard verlässt, wird
 * dank gcTime der Polling-Hook unmounted und cleanup läuft.
 */

const SORT_KEYS = ['severity', 'customer', 'hostname', 'service', 'age'] as const;

const searchSchema = z.object({
  q: z.string().optional(),
  customer: z.string().optional(),
  service: z.string().optional(),
  severity: z.enum(['info', 'warning', 'critical']).optional(),
  kind: z.enum(['none', 'patch', 'minor', 'major', 'unknown']).optional(),
  driftOnly: z.boolean().optional(),
  eolOnly: z.boolean().optional(),
  minAge: z.number().int().nonnegative().optional(),
  sort: z.enum(SORT_KEYS).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
});

type SearchParams = z.infer<typeof searchSchema>;

function searchToFilter(s: SearchParams): DriftFilter {
  return {
    ...(s.q ? { searchText: s.q } : {}),
    ...(s.customer ? { customerId: s.customer } : {}),
    ...(s.service ? { serviceType: s.service } : {}),
    ...(s.severity ? { severity: s.severity } : {}),
    ...(s.kind ? { driftKind: s.kind } : {}),
    ...(s.driftOnly ? { driftOnly: true } : {}),
    ...(s.eolOnly ? { eolOnly: true } : {}),
    ...(s.minAge !== undefined ? { minAgeDays: s.minAge } : {}),
  };
}

function filterToSearch(f: DriftFilter): Omit<SearchParams, 'sort' | 'dir'> {
  return {
    ...(f.searchText ? { q: f.searchText } : {}),
    ...(f.customerId ? { customer: f.customerId } : {}),
    ...(f.serviceType ? { service: f.serviceType } : {}),
    ...(f.severity ? { severity: f.severity } : {}),
    ...(f.driftKind ? { kind: f.driftKind } : {}),
    ...(f.driftOnly ? { driftOnly: true } : {}),
    ...(f.eolOnly ? { eolOnly: true } : {}),
    ...(f.minAgeDays !== undefined ? { minAge: f.minAgeDays } : {}),
  };
}

export const Route = createFileRoute('/drift/')({
  component: DriftDashboard,
  validateSearch: (s) => searchSchema.parse(s),
});

function DriftDashboard(): JSX.Element {
  // --- Filter + Sortierung leben in der URL (Deeplink/Reload). ---
  const search = Route.useSearch();
  const navigate = useNavigate({ from: '/drift/' });
  const filter = useMemo<DriftFilter>(() => searchToFilter(search), [search]);
  const sort = useMemo<DriftSort | undefined>(
    () => (search.sort ? { key: search.sort, dir: search.dir ?? 'asc' } : undefined),
    [search.sort, search.dir]
  );

  const setFilter = (next: DriftFilter): void => {
    void navigate({
      replace: true,
      search: () => ({
        ...filterToSearch(next),
        ...(search.sort ? { sort: search.sort, dir: search.dir ?? 'asc' } : {}),
      }),
    });
  };

  // Klick-Zyklus pro Spalte: asc -> desc -> unsortiert (Backend-Reihenfolge).
  const onSortChange = (key: DriftSortKey): void => {
    let sortPatch: Pick<SearchParams, 'sort' | 'dir'> = {};
    if (search.sort !== key) sortPatch = { sort: key, dir: 'asc' };
    else if ((search.dir ?? 'asc') === 'asc') sortPatch = { sort: key, dir: 'desc' };
    void navigate({
      replace: true,
      search: () => ({ ...filterToSearch(filter), ...sortPatch }),
    });
  };

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [drawerRow, setDrawerRow] = useState<DriftRow | null>(null);

  const driftQuery = useDriftReport(filter);
  const customersQuery = useCustomers({ limit: 200 });
  const serviceTypesQuery = useServiceTypes();

  // --- Re-Scan-Job ---
  const runHealthChecks = useRunHealthChecks();
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const job = useHealthCheckJob(activeJobId);

  // Sobald der Job fertig ist, refetchen wir den Drift-Report einmal & räumen
  // den Job-State auf, damit das Polling stoppt.
  useEffect(() => {
    if (!job.data) return;
    if (job.data.status === 'completed' || job.data.status === 'failed') {
      void driftQuery.refetch();
      // Kleiner Delay, damit der "Job fertig"-Toast noch sichtbar bleibt.
      const t = window.setTimeout(() => setActiveJobId(null), 4_000);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [job.data, driftQuery]);

  // Backend liefert alle Zeilen (server-seitig gefiltert nach Kunde /
  // Service-Typ / nur-Drift). Severity, EOL, Drift-Typ, Freitext und
  // Beobachtungs-Alter wenden wir clientseitig an, danach Sortierung.
  const allRows = useMemo(() => driftQuery.data?.data ?? [], [driftQuery.data]);
  const rows = useMemo(
    () => sortDriftRows(applyDriftClientFilters(allRows, filter), sort),
    [allRows, filter, sort]
  );
  const stats = driftQuery.data?.stats ?? {
    total_observed: 0,
    drifted: 0,
    eol: 0,
    stale: 0,
  };

  // --- Selection-Helpers ---
  const allVisibleIds = useMemo(() => rows.map((r) => r.system_id), [rows]);

  // Filterwechsel darf keine unsichtbaren Zeilen in der Auswahl zurücklassen -
  // sonst würde "Re-Scan ausgewaehlte" heimlich mehr treffen als angezeigt.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(allVisibleIds);
      const next = new Set<string>();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [allVisibleIds]);
  const allSelected =
    selectionMode &&
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedIds.has(id));

  const toggleAll = (): void => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allVisibleIds));
    }
  };

  const toggleRow = (systemId: string): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(systemId)) next.delete(systemId);
      else next.add(systemId);
      return next;
    });
  };

  const exitSelection = (): void => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  };

  // --- Re-Scan-Actions ---
  const onRescanSelected = (): void => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    runHealthChecks.mutate(
      { systemIds: ids },
      {
        onSuccess: (res) => {
          setActiveJobId(res.job_id);
          exitSelection();
        },
      }
    );
  };

  // Das Backend akzeptiert im Re-Scan-Body nur customer_id ODER system_ids -
  // KEIN service_type (sonst 422). planFilteredRescan kapselt diese Regel:
  // sobald ein Filter aktiv ist, den das Backend nicht als Re-Scan-Kriterium
  // kennt, werden die konkreten system_ids der sichtbaren Zeilen geschickt.
  const rescanPlan = useMemo<FilteredRescanPlan>(
    () => planFilteredRescan(filter, rows),
    [filter, rows]
  );

  const onRescanFiltered = (): void => {
    if (rescanPlan.empty) return;
    runHealthChecks.mutate(rescanPlan.input, {
      onSuccess: (res) => setActiveJobId(res.job_id),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold">Drift-Dashboard</h1>
          </div>
          <p className="text-muted">
            Soll- vs. Ist-Versionen aller beobachteten Systeme. Letzter Bericht:{' '}
            {driftQuery.data?.generated_at
              ? formatDateTime(driftQuery.data.generated_at)
              : '–'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            leftIcon={<RefreshCw className="size-4" />}
            onClick={() => void driftQuery.refetch()}
            loading={driftQuery.isFetching}
          >
            Aktualisieren
          </Button>
        </div>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard
          label="Beobachtete Systeme"
          value={stats.total_observed}
          icon={<Eye className="size-5" />}
          tone="neutral"
          testId="stat-total"
        />
        <StatCard
          label="Drift (warn + crit)"
          value={stats.drifted}
          icon={<AlertTriangle className="size-5" />}
          tone={stats.drifted > 0 ? 'warning' : 'neutral'}
          testId="stat-drift"
        />
        <StatCard
          label="EOL"
          value={stats.eol}
          icon={<ServerCrash className="size-5" />}
          tone={stats.eol > 0 ? 'danger' : 'neutral'}
          testId="stat-eol"
        />
        <StatCard
          label="Stale (>7 Tage)"
          value={stats.stale}
          icon={<Clock className="size-5" />}
          tone={stats.stale > 0 ? 'warning' : 'neutral'}
          helpText="Letzte Beobachtung > 7 Tage alt"
          testId="stat-stale"
        />
      </div>

      {/* Job-Status */}
      {activeJobId && job.data && <RescanJobBanner job={job.data} />}

      {/* Filter + Toolbar */}
      <Card>
        <DriftFilterBar
          filter={filter}
          onChange={setFilter}
          customerOptions={customersQuery.data?.data ?? []}
          // Defensiv: der geteilte useServiceTypes-Hook (außerhalb dieses
          // Bereichs) liefert je nach Endpunkt-Stand entweder ein Array oder
          // {data,total}. Bei Nicht-Array leere Optionen statt Crash.
          serviceTypeOptions={
            Array.isArray(serviceTypesQuery.data) ? serviceTypesQuery.data : []
          }
          resultCount={rows.length}
          totalCount={allRows.length}
        />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={selectionMode ? 'primary' : 'secondary'}
              leftIcon={selectionMode ? <CheckSquare className="size-4" /> : <Square className="size-4" />}
              onClick={() => {
                setSelectionMode((v) => !v);
                if (selectionMode) setSelectedIds(new Set());
              }}
              data-testid="drift-selection-toggle"
            >
              {selectionMode ? 'Auswahl-Mode aktiv' : 'Auswahl-Mode'}
            </Button>
            {selectionMode && (
              <span className="text-xs text-muted">
                {selectedIds.size} / {rows.length} markiert
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              leftIcon={<PlayCircle className="size-4" />}
              disabled={!selectionMode || selectedIds.size === 0}
              loading={runHealthChecks.isPending}
              onClick={onRescanSelected}
              data-testid="drift-rescan-selected"
            >
              Re-Scan ausgewaehlte ({selectedIds.size})
            </Button>
            <Button
              size="sm"
              leftIcon={<Activity className="size-4" />}
              loading={runHealthChecks.isPending}
              disabled={rescanPlan.empty}
              onClick={onRescanFiltered}
              data-testid="drift-rescan-filtered"
            >
              {rescanPlan.scope === 'all' && 'Re-Scan alle Systeme'}
              {rescanPlan.scope === 'customer' && 'Re-Scan Kunde (alle Systeme)'}
              {rescanPlan.scope === 'systems' &&
                `Re-Scan alle gefilterten (${Math.min(
                  rescanPlan.systemCount ?? 0,
                  RESCAN_MAX_SYSTEMS
                )})`}
            </Button>
          </div>
        </div>

        {rescanPlan.truncated && (
          <p className="mt-2 text-xs text-warning" data-testid="drift-rescan-truncated">
            Re-Scan mit expliziten Systemen ist auf {RESCAN_MAX_SYSTEMS} pro Lauf
            begrenzt — der Filter trifft {rescanPlan.systemCount} Systeme. Filter
            enger setzen oder mehrfach starten.
          </p>
        )}

        {runHealthChecks.isError && (
          <ErrorAlert error={runHealthChecks.error} className="mt-3" />
        )}
      </Card>

      {/* Tabelle */}
      <Card title="Drift-Liste">
        {driftQuery.isLoading && <SkeletonRows rows={6} cols={7} />}
        {driftQuery.isError && <ErrorAlert error={driftQuery.error} />}
        {!driftQuery.isLoading && !driftQuery.isError && rows.length === 0 && (
          <EmptyState
            icon={<Eye className="size-8" />}
            title="Keine Drift-Daten"
            description="Entweder es läuft noch kein Health-Check, alle Systeme sind in-sync, oder der Filter ist zu eng. Re-Scan auslosen oder Filter anpassen."
          />
        )}
        {rows.length > 0 && (
          <DriftTable
            rows={rows}
            selectionMode={selectionMode}
            selectedIds={selectedIds}
            onToggleAll={toggleAll}
            onToggleRow={toggleRow}
            allSelected={allSelected}
            onRowClick={(row) => setDrawerRow(row)}
            sort={sort}
            onSortChange={onSortChange}
          />
        )}
      </Card>

      <DriftDetailDrawer row={drawerRow} onClose={() => setDrawerRow(null)} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Re-Scan-Job-Banner
// ---------------------------------------------------------------------------

function RescanJobBanner({ job }: { job: HealthCheckJob }): JSX.Element {
  const { status } = job;
  // Das Backend füllt die Zähler erst nach Abschluss (während des Laufs sind
  // alle Counts 0, report=None). Solange queued/running -> indeterminate.
  const running = status === 'queued' || status === 'running';

  const tone =
    status === 'failed'
      ? 'border-danger/30 bg-danger/5 text-danger'
      : status === 'completed'
        ? 'border-success/30 bg-success/5 text-success'
        : 'border-primary/30 bg-primary/5 text-primary';

  let message: string;
  if (status === 'failed') {
    message = job.error ? `Re-Scan fehlgeschlagen: ${job.error}` : 'Re-Scan fehlgeschlagen';
  } else if (status === 'completed') {
    const errors = job.systemsWithErrors;
    message =
      `Re-Scan abgeschlossen - ${job.systemsOk}/${job.systemsTotal} Systeme OK` +
      (errors > 0 ? `, ${errors} mit Fehlern` : '');
  } else {
    message = 'Re-Scan läuft…';
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="drift-rescan-banner"
      className={['flex items-start gap-2 rounded-md border p-3 text-sm', tone].join(' ')}
    >
      <Activity
        className={['mt-0.5 size-4 shrink-0', running ? 'animate-spin' : ''].join(' ')}
        aria-hidden="true"
      />
      <div className="flex-1">
        <p className="font-medium" data-testid="drift-rescan-message">
          {message}
        </p>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
          {running ? (
            // Indeterminate: es liegen (noch) keine belastbaren Counts vor.
            <div
              className="h-full w-1/3 animate-pulse rounded-full bg-current"
              aria-hidden="true"
            />
          ) : (
            <div
              className="h-full rounded-full bg-current transition-all"
              style={{
                width: `${
                  job.systemsTotal > 0
                    ? Math.round((job.systemsOk / job.systemsTotal) * 100)
                    : 100
                }%`,
              }}
              aria-hidden="true"
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// DriftTable
// ---------------------------------------------------------------------------

interface DriftTableProps {
  rows: DriftRow[];
  selectionMode: boolean;
  selectedIds: Set<string>;
  allSelected: boolean;
  onToggleAll: () => void;
  onToggleRow: (systemId: string) => void;
  onRowClick: (row: DriftRow) => void;
  /** Aktive Sortierung; ohne onSortChange bleiben die Header statisch. */
  sort?: DriftSort | undefined;
  onSortChange?: ((key: DriftSortKey) => void) | undefined;
}

function SortableTH({
  label,
  sortKey,
  sort,
  onSortChange,
}: {
  label: string;
  sortKey: DriftSortKey;
  sort?: DriftSort | undefined;
  onSortChange?: ((key: DriftSortKey) => void) | undefined;
}): JSX.Element {
  // DataTable setzt das <th> selbst; hier nur der Kopfzellen-Inhalt.
  if (!onSortChange) return <>{label}</>;
  const active = sort?.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onSortChange(sortKey)}
      className="inline-flex items-center gap-1 uppercase hover:text-text"
      aria-label={`Nach ${label} sortieren`}
      data-testid={`drift-sort-${sortKey}`}
    >
      {label}
      <Icon
        className={['size-3.5', active ? '' : 'opacity-40'].join(' ')}
        aria-hidden="true"
      />
    </button>
  );
}

export function DriftTable({
  rows,
  selectionMode,
  selectedIds,
  allSelected,
  onToggleAll,
  onToggleRow,
  onRowClick,
  sort,
  onSortChange,
}: DriftTableProps): JSX.Element {
  const columns = useMemo<ReadonlyArray<Column<DriftRow>>>(() => {
    const checkboxColumn: Column<DriftRow> = {
      id: 'auswahl',
      header: (
        <input
          type="checkbox"
          checked={allSelected}
          onChange={onToggleAll}
          className="size-4 rounded border-border accent-primary"
          aria-label="Alle markieren"
          data-testid="drift-select-all"
        />
      ),
      headerClassName: 'w-8',
      filter: false,
      filterLabel: 'Auswahl',
      cell: (row) => (
        <input
          type="checkbox"
          checked={selectedIds.has(row.system_id)}
          onChange={() => onToggleRow(row.system_id)}
          onClick={(e) => e.stopPropagation()}
          className="size-4 rounded border-border accent-primary"
          aria-label={`${row.system_hostname} markieren`}
          data-testid={`drift-row-select-${row.system_id}`}
        />
      ),
    };

    const dataColumns: ReadonlyArray<Column<DriftRow>> = [
      {
        id: 'severity',
        header: (
          <SortableTH label="Severity" sortKey="severity" sort={sort} onSortChange={onSortChange} />
        ),
        filterLabel: 'Severity',
        filter: 'select',
        filterValue: (row) =>
          row.severity === 'info' ? 'OK' : row.severity === 'warning' ? 'Drift' : 'Kritisch',
        cell: (row) => <SeverityBadge severity={row.severity} />,
      },
      {
        id: 'kunde',
        header: (
          <SortableTH label="Kunde" sortKey="customer" sort={sort} onSortChange={onSortChange} />
        ),
        filterLabel: 'Kunde',
        cell: (row) => <span className="font-medium">{row.customer_name}</span>,
      },
      {
        id: 'hostname',
        header: (
          <SortableTH label="Hostname" sortKey="hostname" sort={sort} onSortChange={onSortChange} />
        ),
        filterLabel: 'Hostname',
        cell: (row) => <span className="font-mono text-xs">{row.system_hostname}</span>,
      },
      {
        id: 'service',
        header: (
          <SortableTH
            label="Service-Typ"
            sortKey="service"
            sort={sort}
            onSortChange={onSortChange}
          />
        ),
        filterLabel: 'Service-Typ',
        filter: 'select',
        filterValue: (row) => row.service_type_label ?? row.service_type,
        cell: (row) => {
          const Icon = iconForServiceType(row.service_type);
          return (
            <span className="inline-flex items-center gap-1.5">
              <Icon className="size-4 text-muted" aria-hidden="true" />
              <span className="text-xs">{row.service_type_label ?? row.service_type}</span>
            </span>
          );
        },
      },
      {
        id: 'ist-version',
        header: 'Ist-Version',
        filterValue: (row) => row.current_version ?? '',
        cell: (row) => (
          <code className="rounded bg-surface-2 px-1 py-0.5 text-xs">
            {row.current_version ?? '–'}
          </code>
        ),
      },
      {
        id: 'soll-version',
        header: 'Soll-Version',
        filterValue: (row) => row.target_version ?? '',
        cell: (row) => (
          <code className="rounded bg-surface-2 px-1 py-0.5 text-xs">
            {row.target_version ?? '–'}
          </code>
        ),
      },
      {
        id: 'diff',
        header: 'Diff',
        filterValue: (row) => row.drift_diff ?? '',
        cell: (row) =>
          row.drift_diff ? (
            <span className="font-mono text-xs">{row.drift_diff}</span>
          ) : (
            <span className="text-xs text-muted">–</span>
          ),
      },
      {
        id: 'age',
        header: <SortableTH label="Age" sortKey="age" sort={sort} onSortChange={onSortChange} />,
        filterLabel: 'Age',
        className: 'text-xs',
        filterValue: (row) => row.last_seen_at ?? '',
        cell: (row) => <RelativeTime value={row.last_seen_at} />,
      },
      {
        id: 'eol',
        header: 'EOL',
        filter: 'select',
        filterValue: (row) => (row.is_eol ? 'EOL' : ''),
        cell: (row) =>
          row.is_eol ? (
            <Badge tone="danger" data-testid={`drift-eol-${row.system_id}`}>
              EOL
            </Badge>
          ) : (
            <span className="text-xs text-muted">–</span>
          ),
      },
    ];

    return selectionMode ? [checkboxColumn, ...dataColumns] : dataColumns;
  }, [
    selectionMode,
    selectedIds,
    allSelected,
    onToggleAll,
    onToggleRow,
    sort,
    onSortChange,
  ]);

  return (
    <DataTable
      caption="Drift-Liste"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      storageKey="drift"
      onRowClick={(row) => {
        // Selection-Mode: Zeilenklick markiert, sonst öffnet der Drawer.
        if (selectionMode) {
          onToggleRow(row.system_id);
          return;
        }
        onRowClick(row);
      }}
      rowSelected={(row) => selectedIds.has(row.system_id)}
      rowTestId={(row) => `drift-row-${row.system_id}`}
    />
  );
}

// ---------------------------------------------------------------------------
// DriftDetailDrawer
// ---------------------------------------------------------------------------

function DriftDetailDrawer({
  row,
  onClose,
}: {
  row: DriftRow | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Drawer
      open={Boolean(row)}
      onClose={onClose}
      title={row ? `${row.customer_name} – ${row.system_hostname}` : 'Drift-Details'}
      description={
        row ? (
          <span className="flex flex-wrap items-center gap-2">
            <SeverityBadge severity={row.severity} />
            {row.is_eol && <Badge tone="danger">EOL</Badge>}
            <span className="text-xs">{row.service_type_label ?? row.service_type}</span>
          </span>
        ) : undefined
      }
    >
      {row && (
        <div className="flex flex-col gap-4">
          <section>
            <h3 className="mb-2 text-sm font-medium">Versionen</h3>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              <DefRow label="Ist-Version" value={row.current_version ?? '–'} mono />
              <DefRow label="Soll-Version" value={row.target_version ?? '–'} mono />
              <DefRow label="Diff" value={row.drift_diff ?? '–'} mono />
              <DefRow label="Detection" value={row.detection_method ?? '–'} mono />
              <DefRow
                label="Letzte Beobachtung"
                value={
                  <RelativeTime value={row.last_seen_at} variant="absolute-with-tooltip" />
                }
              />
              <DefRow label="EOL" value={row.eol_at ? formatDateTime(row.eol_at) : '–'} />
            </dl>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-medium">Raw stdout</h3>
            <pre
              className="max-h-64 overflow-auto rounded-md border border-border bg-surface-2 p-2 text-xs"
              data-testid="drift-raw-stdout"
            >
              {row.raw_stdout ?? '(kein Output erfasst)'}
            </pre>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-medium">History (letzte 5)</h3>
            {!row.history || row.history.length === 0 ? (
              <p className="text-sm text-muted">Keine History verfügbar.</p>
            ) : (
              <ul className="flex flex-col gap-1 text-xs" data-testid="drift-history">
                {row.history.slice(0, 5).map((h, i) => (
                  <li
                    key={`${h.observed_at}-${i}`}
                    className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1"
                  >
                    <RelativeTime value={h.observed_at} variant="absolute-with-tooltip" />
                    <code className="text-xs">{h.version ?? '–'}</code>
                    <span className="text-muted">{h.detection_method ?? '–'}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

function DefRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  );
}

// Re-export für Tests / Konsumenten außerhalb der Route.
// (Vitest braucht ein Default-Export der DriftTable, daher named export oben.)
export { DriftDashboard };
